import {
    GameContextType,
    GameState,
    StatusState,
    PromotionState,
    RemoteNavigationState,
} from "./gameContextTypes";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { getCapturedPiecesFromFEN } from "../components/visual/CapturedPieces/CapturePieces";

export const GameContext = createContext<GameContextType | null>(null);

export const useGame = () => {
    const context = useContext(GameContext);
    if (!context) {
        throw new Error("useGame must be used within a GameProvider");
    }
    return context;
};

export const GameProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [showMultiplayerMenu, setShowMultiplayerMenu] = useState<boolean>(false);
    const [remoteNavigation, setRemoteNavigation] = useState<RemoteNavigationState>({
        remoteOverSquare: "e2", // Preselect E2
        remoteClickSquare: null,
        isInMenuIcons: false,
        selectedMenuIcon: null,
        isInGameOverMenu: false,
        selectedGameOverIcon: null,
    });
    const [status, setStatus] = useState<StatusState>({
        showStatusPage: false,
    });
    const [gameState, setGameState] = useState<GameState>({
        game: null,
        lastMove: null,
        isAIGame: false,
        aiDifficulty: "Medium",
        showGame: false,
        showInGameMenu: false,
        capturedPieces: {
            white: [],
            black: [],
        },
        volume: "Medium",
        isReviewMode: false,
    });

    useEffect(() => {
        if (!gameState.game) return;

        const newCaptured = getCapturedPiecesFromFEN(gameState.game.fen());
        setGameState((prev) => ({
            ...prev,
            capturedPieces: newCaptured,
        }));
    }, [gameState.game?.fen()]); // Watch the FEN string instead of the game object

    const [promotion, setPromotion] = useState<PromotionState>({
        open: false,
        type: null,
    });

    const contextValue: GameContextType = {
        showMultiplayerMenu,
        setShowMultiplayerMenu,
        remoteNavigation,
        setRemoteNavigation,
        status,
        setStatus,
        gameState,
        setGameState,
        promotion,
        setPromotion,
    };

    return <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>;
};

export default GameProvider;
